import type { PostgrestClient } from "@supabase/postgrest-js";
import type { DatabaseWithOptions } from "../../../__definitions__/database-definitions";
import { usePermissions } from "../../../server/auth/use-permissions";
import { useSupabaseLoader } from "../../../server/supabase/hooks";
import type { UseSupabaseLoaderState } from "../../../server/supabase/hooks/types";
import type { UUID } from "../../../utils/uuid";
import { SupabaseCustomEmailService } from "../../channels/email/server/supabase-custom-email-service";
import type { ChannelName } from "../../channels/types";
import { SupabaseEventService, type EventKey } from "../../event/server/supabase-event-service";
import { SupabaseAccountService } from "../../generic/server/supabase-account-service";
import { SupabaseMergeTagService } from "../../merge-tag/server/supabase-merge-tag-service";
import type { Tags } from "../../merge-tag/types";
import {
  getMomentRestrictions,
  type MomentRestrictions,
} from "../../moment/hooks/use-moment-restrictions";
import type { GetPersonsInSegmentJsonPerson } from "../../segment/components/manager-v2/types";
import { SupabaseSlackAppService } from "../../slack/server/slack-supabase-service";
import type { FollowUpDetailsWithConfig, FollowUpMomentDetails } from "../server/types";
import { isFollowUpDetailsWithConfig } from "../utils";

export type FollowUpMomentDetailsConfig =
  | { type: "follow_up"; data: UUID }
  | { type: "from_moment"; data: { moment_id: UUID } }
  | { type: "from_survey"; data: { moment_id: UUID; survey_id: UUID } };

export type FollowUpSegmentPerson = Omit<GetPersonsInSegmentJsonPerson, "channels"> & {
  channels: Partial<Record<NonNullable<ChannelName>, string>>;
};

export type FollowUpMomentDetailsLoaderData = {
  momentFollowUpDetails: FollowUpMomentDetails;
  enabledChannels: NonNullable<ChannelName>[];
  segmentPersons?: FollowUpSegmentPerson[];
  mergeTags: Tags;
  slackVideoEnabled: boolean;
  restrictions: MomentRestrictions;
  customEmailAddresses: { id: UUID; address: string }[];
  followUp?: FollowUpDetailsWithConfig;
};

export function useFollowUpMomentDetails(
  config: FollowUpMomentDetailsConfig,
): UseSupabaseLoaderState<FollowUpMomentDetailsLoaderData> {
  const { ce_admin } = usePermissions();

  return useSupabaseLoader(
    async ({ supabase, account_id }) => {
      const [
        { momentFollowUpDetails, followUp },
        enabledChannels,
        mergeTags,
        slackVideoEnabled,
        restrictions,
        customEmailAddresses,
      ] = await Promise.all([
        loadMomentAndFollowUp(supabase, account_id, config),
        new SupabaseAccountService(supabase).getEnabledChannels(account_id),
        new SupabaseMergeTagService(supabase).getAllMergeTags(),
        new SupabaseSlackAppService(supabase).videoEnabled(account_id),
        getMomentRestrictions(ce_admin, supabase),
        new SupabaseCustomEmailService(supabase).getAllVerified(account_id),
      ]);

      if (momentFollowUpDetails.segment?.segment_json) {
        const { data: persons, error: err } = await supabase.rpc("get_persons_in_segment_json", {
          account_id_: account_id,
          segment_json: momentFollowUpDetails.segment.segment_json,
          target_manager_: momentFollowUpDetails.target_manager ?? undefined,
          target_connection_type_: momentFollowUpDetails.target_connection_type ?? undefined,
        });

        if (err) {
          console.error("Error loading persons for Follow-Up segment:", err);
          throw err;
        }

        return {
          momentFollowUpDetails,
          enabledChannels,
          segmentPersons: persons.map((p) => ({
            ...p,
            channels: p.channels as Partial<Record<NonNullable<ChannelName>, string>>,
          })),
          mergeTags,
          slackVideoEnabled,
          followUp,
          restrictions,
          customEmailAddresses: customEmailAddresses.data ?? [],
        };
      }

      return {
        momentFollowUpDetails,
        enabledChannels,
        mergeTags,
        slackVideoEnabled,
        followUp,
        restrictions,
        customEmailAddresses: customEmailAddresses.data ?? [],
      };
    },
    [config, ce_admin],
  );
}

async function loadMomentAndFollowUp(
  supabase: PostgrestClient<DatabaseWithOptions["db"], DatabaseWithOptions["options"], "public">,
  account_id: UUID,
  config: FollowUpMomentDetailsConfig,
): Promise<Pick<FollowUpMomentDetailsLoaderData, "momentFollowUpDetails" | "followUp">> {
  switch (config.type) {
    case "from_moment":
    case "from_survey":
      return loadFromMomentId(supabase, config.data.moment_id, account_id);
    case "follow_up":
      return loadFromFollowUpId(supabase, account_id, config.data);
  }
}

async function loadFromFollowUpId(
  supabase: PostgrestClient<DatabaseWithOptions["db"], DatabaseWithOptions["options"], "public">,
  account_id: UUID,
  follow_up_id: UUID,
): Promise<Pick<FollowUpMomentDetailsLoaderData, "momentFollowUpDetails" | "followUp">> {
  const { data: followUp, error: followUpError } = await supabase
    .rpc("follow_up_details", {
      follow_up_id_: follow_up_id,
      account_id_: account_id,
    })
    .single();

  if (followUpError) {
    console.error("Error loading Follow-Up:", followUpError);
    throw followUpError;
  }

  if (!isFollowUpDetailsWithConfig(followUp)) {
    throw new Error("Follow up does not have a linked Communication");
  }

  if (followUp.schedule?.event_key) {
    const { data: events, error: eventError } = await new SupabaseEventService(supabase).getKeys({
      mustIncludeKey: followUp.schedule.event_key,
      includeLibrary: false,
    });
    if (eventError) throw eventError;
    followUp.schedule_event =
      (events?.find((e) => e.key === followUp.schedule?.event_key) as EventKey) ?? null;
  }

  const { data: momentFollowUpDetails, error: momentFollowUpDetailsError } = await supabase
    .rpc("follow_up_moment_details", {
      moment_id_: followUp.config.source_id as UUID,
      account_id_: account_id,
    })
    .single();

  if (momentFollowUpDetailsError) throw momentFollowUpDetailsError;

  return {
    momentFollowUpDetails,
    followUp,
  };
}

async function loadFromMomentId(
  supabase: PostgrestClient<DatabaseWithOptions["db"], DatabaseWithOptions["options"], "public">,
  moment_id: UUID,
  account_id: UUID,
): Promise<Pick<FollowUpMomentDetailsLoaderData, "momentFollowUpDetails">> {
  const [{ data: momentFollowUpDetails, error: momentFollowUpDetailsError }] = await Promise.all([
    supabase
      .rpc("follow_up_moment_details", {
        moment_id_: moment_id,
        account_id_: account_id,
      })
      .single(),
  ]);

  if (momentFollowUpDetailsError) throw momentFollowUpDetailsError;

  return {
    momentFollowUpDetails,
  };
}
